// Singleton Automation State Manager
// This persists automation state across page navigations

export const MAX_LIVE_AUTOMATION_INSTANCES_PER_TYPE = 4;

const hasSnapshotContent = (snapshot) =>
  Boolean(snapshot && typeof snapshot === "object" && Object.keys(snapshot).length > 0);

const getLiveAutomationId = (snapshot = {}, fallbackPrefix = "automation") =>
  String(
    snapshot.sequenceRuntimeEventKey
      || snapshot.quickAutomationId
      || snapshot.countdownId
      || snapshot.reminderId
      || snapshot.id
      || `${fallbackPrefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );

const reviveSnapshotDates = (snapshot = {}) => {
  if (!snapshot || typeof snapshot !== "object") return snapshot;
  const revived = { ...snapshot };
  if (revived.startTime) {
    revived.startTime = new Date(revived.startTime);
  }
  return revived;
};

const prepareSnapshotForStorage = (snapshot = {}) => {
  if (!snapshot || typeof snapshot !== "object") return snapshot;
  const prepared = { ...snapshot };
  if (prepared.startTime instanceof Date) {
    prepared.startTime = prepared.startTime.toISOString();
  }
  return prepared;
};

const normalizeLiveAutomationEntry = (entry = {}, fallbackPrefix = "automation") => {
  const snapshot = reviveSnapshotDates(entry.snapshot || entry);
  const id = String(entry.id || getLiveAutomationId(snapshot, fallbackPrefix));
  return { id, snapshot };
};

const prepareLiveAutomationEntryForStorage = (entry = {}) => ({
  ...entry,
  snapshot: prepareSnapshotForStorage(entry.snapshot || {}),
});

class AutomationStateManager {
  constructor() {
    this.state = {
      // AutoNavigate state
      nextClass: null,
      timeUntilNavigation: null,
      navigationScheduled: false,
      
      // AutoCountdown state
      activeCountdowns: [],
      liveCountdowns: [],
      countdownSnapshot: {},
      showCountdownModal: false,
      isFlashing: false,
      
      // AutoReminder state
      activeReminders: [],
      liveReminders: [],
      reminderSnapshot: {},
      showReminderModal: false,
      
      // AutoMusic state
      activeSegments: [],
      segmentSnapshot: {},
      showMusicModal: false,
      showVideoMusicModal: false,
      isPaused: false,
      
      // Routine birthday state
      birthdays: [],
      showBirthdayModal: false,
      
      // Shared state
      currentSystemTime: new Date(),
      throttled: false,
      
      // Navigation coordination
      activeAutomations: []
    };
    
    this.listeners = [];
    this.loadFromStorage();
  }

  // Load state from localStorage
  loadFromStorage() {
    try {
      const stored = localStorage.getItem('automationState');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convert date strings back to Date objects
        if (parsed.currentSystemTime) {
          parsed.currentSystemTime = new Date(parsed.currentSystemTime);
        }
        // Convert startTime in snapshots back to Date objects
        if (parsed.countdownSnapshot?.startTime) {
          parsed.countdownSnapshot.startTime = new Date(parsed.countdownSnapshot.startTime);
        }
        if (parsed.reminderSnapshot?.startTime) {
          parsed.reminderSnapshot.startTime = new Date(parsed.reminderSnapshot.startTime);
        }
        parsed.liveCountdowns = Array.isArray(parsed.liveCountdowns)
          ? parsed.liveCountdowns.map((entry) => normalizeLiveAutomationEntry(entry, "countdown"))
          : [];
        parsed.liveReminders = Array.isArray(parsed.liveReminders)
          ? parsed.liveReminders.map((entry) => normalizeLiveAutomationEntry(entry, "reminder"))
          : [];
        if (!parsed.liveCountdowns.length && parsed.showCountdownModal && hasSnapshotContent(parsed.countdownSnapshot)) {
          parsed.liveCountdowns = [
            normalizeLiveAutomationEntry({ snapshot: parsed.countdownSnapshot }, "countdown"),
          ];
        }
        if (!parsed.liveReminders.length && parsed.showReminderModal && hasSnapshotContent(parsed.reminderSnapshot)) {
          parsed.liveReminders = [
            normalizeLiveAutomationEntry({ snapshot: parsed.reminderSnapshot }, "reminder"),
          ];
        }
        parsed.countdownSnapshot = parsed.liveCountdowns[0]?.snapshot || parsed.countdownSnapshot || {};
        parsed.reminderSnapshot = parsed.liveReminders[0]?.snapshot || parsed.reminderSnapshot || {};
        parsed.showCountdownModal = parsed.liveCountdowns.length > 0;
        parsed.showReminderModal = parsed.liveReminders.length > 0;
        if (parsed.segmentSnapshot?.startTime) {
          parsed.segmentSnapshot.startTime = new Date(parsed.segmentSnapshot.startTime);
        }
        if (parsed.nextClass) {
          parsed.nextClass = { ...parsed.nextClass };
          if (parsed.nextClass.transitionTime) {
            parsed.nextClass.transitionTime = new Date(parsed.nextClass.transitionTime);
          }
          if (parsed.nextClass.startTime) {
            parsed.nextClass.startTime = new Date(parsed.nextClass.startTime);
          }
        }
        this.state = { ...this.state, ...parsed };
        
        // Ensure flash state is always false when loading from storage
        this.state.isFlashing = false;
      }
    } catch (error) {
      console.warn('Failed to load automation state from storage:', error);
    }
  }

  // Save state to localStorage
  saveToStorage() {
    try {
      const stateToSave = { ...this.state };
      
      // Exclude flash state from persistence - it should not survive page reloads
      delete stateToSave.isFlashing;
      
      // Convert Date objects to strings for storage
      if (stateToSave.currentSystemTime) {
        stateToSave.currentSystemTime = stateToSave.currentSystemTime.toISOString();
      }
      // Convert startTime in snapshots to strings
      if (stateToSave.countdownSnapshot?.startTime) {
        stateToSave.countdownSnapshot = { ...stateToSave.countdownSnapshot };
        stateToSave.countdownSnapshot.startTime = stateToSave.countdownSnapshot.startTime.toISOString();
      }
      if (stateToSave.reminderSnapshot?.startTime) {
        stateToSave.reminderSnapshot = { ...stateToSave.reminderSnapshot };
        stateToSave.reminderSnapshot.startTime = stateToSave.reminderSnapshot.startTime.toISOString();
      }
      if (Array.isArray(stateToSave.liveCountdowns)) {
        stateToSave.liveCountdowns = stateToSave.liveCountdowns.map(prepareLiveAutomationEntryForStorage);
      }
      if (Array.isArray(stateToSave.liveReminders)) {
        stateToSave.liveReminders = stateToSave.liveReminders.map(prepareLiveAutomationEntryForStorage);
      }
      if (stateToSave.segmentSnapshot?.startTime) {
        stateToSave.segmentSnapshot = { ...stateToSave.segmentSnapshot };
        stateToSave.segmentSnapshot.startTime = stateToSave.segmentSnapshot.startTime.toISOString();
      }
      localStorage.setItem('automationState', JSON.stringify(stateToSave));
    } catch (error) {
      console.warn('Failed to save automation state to storage:', error);
    }
  }

  // Subscribe to state changes
  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  // Notify all listeners of state changes
  notify() {
    this.listeners.forEach(listener => listener(this.state));
  }

  // Update state
  updateState(updates) {
    this.state = { ...this.state, ...updates };
    this.saveToStorage();
    this.notify();
  }

  // Note: Navigation is never blocked - all automations trigger exactly when scheduled

  // Register an active automation
  registerActiveAutomation(automationType) {
    if (!this.state.activeAutomations.includes(automationType)) {
      this.updateState({
        activeAutomations: [...this.state.activeAutomations, automationType]
      });
    }
  }

  // Unregister an active automation
  unregisterActiveAutomation(automationType) {
    this.updateState({
      activeAutomations: this.state.activeAutomations.filter(type => type !== automationType)
    });
  }

  // AutoNavigate methods
  setNextClass(nextClass) {
    this.updateState({ nextClass });
  }

  setTimeUntilNavigation(time) {
    this.updateState({ timeUntilNavigation: time });
  }

  setNavigationScheduled(scheduled) {
    this.updateState({ navigationScheduled: scheduled });
  }

  // AutoCountdown methods
  setActiveCountdowns(countdowns) {
    this.updateState({ activeCountdowns: countdowns });
  }

  upsertLiveCountdown(id, snapshot) {
    const entryId = String(id || getLiveAutomationId(snapshot, "countdown"));
    const liveCountdowns = Array.isArray(this.state.liveCountdowns) ? this.state.liveCountdowns : [];
    const existingIndex = liveCountdowns.findIndex((entry) => String(entry.id) === entryId);

    if (existingIndex === -1 && liveCountdowns.length >= MAX_LIVE_AUTOMATION_INSTANCES_PER_TYPE) {
      return false;
    }

    const nextEntry = { id: entryId, snapshot };
    const nextLiveCountdowns = existingIndex >= 0
      ? liveCountdowns.map((entry, index) => (index === existingIndex ? nextEntry : entry))
      : [...liveCountdowns, nextEntry];

    this.updateState({
      liveCountdowns: nextLiveCountdowns,
      countdownSnapshot: nextLiveCountdowns[0]?.snapshot || {},
      showCountdownModal: nextLiveCountdowns.length > 0,
    });
    this.registerActiveAutomation("countdown");
    return true;
  }

  removeLiveCountdown(id) {
    const entryId = String(id || "");
    const nextLiveCountdowns = (Array.isArray(this.state.liveCountdowns) ? this.state.liveCountdowns : [])
      .filter((entry) => String(entry.id) !== entryId);

    this.updateState({
      liveCountdowns: nextLiveCountdowns,
      countdownSnapshot: nextLiveCountdowns[0]?.snapshot || {},
      showCountdownModal: nextLiveCountdowns.length > 0,
      isFlashing: nextLiveCountdowns.length > 0 ? this.state.isFlashing : false,
    });

    if (!nextLiveCountdowns.length) {
      this.unregisterActiveAutomation("countdown");
    }
  }

  clearLiveCountdowns() {
    this.updateState({
      liveCountdowns: [],
      countdownSnapshot: {},
      showCountdownModal: false,
      isFlashing: false,
    });
    this.unregisterActiveAutomation("countdown");
  }

  setCountdownSnapshot(snapshot) {
    this.updateState({ countdownSnapshot: snapshot });
  }

  setShowCountdownModal(show) {
    if (show) {
      if (hasSnapshotContent(this.state.countdownSnapshot)) {
        this.upsertLiveCountdown(
          getLiveAutomationId(this.state.countdownSnapshot, "countdown"),
          this.state.countdownSnapshot
        );
        return;
      }
      this.updateState({ showCountdownModal: true });
      this.registerActiveAutomation("countdown");
      return;
    }
    this.clearLiveCountdowns();
  }

  setIsFlashing(flashing) {
    this.updateState({ isFlashing: flashing });
  }

  // AutoReminder methods
  setActiveReminders(reminders) {
    this.updateState({ activeReminders: reminders });
  }

  upsertLiveReminder(id, snapshot) {
    const entryId = String(id || getLiveAutomationId(snapshot, "reminder"));
    const liveReminders = Array.isArray(this.state.liveReminders) ? this.state.liveReminders : [];
    const existingIndex = liveReminders.findIndex((entry) => String(entry.id) === entryId);

    if (existingIndex === -1 && liveReminders.length >= MAX_LIVE_AUTOMATION_INSTANCES_PER_TYPE) {
      return false;
    }

    const nextEntry = { id: entryId, snapshot };
    const nextLiveReminders = existingIndex >= 0
      ? liveReminders.map((entry, index) => (index === existingIndex ? nextEntry : entry))
      : [...liveReminders, nextEntry];

    this.updateState({
      liveReminders: nextLiveReminders,
      reminderSnapshot: nextLiveReminders[0]?.snapshot || {},
      showReminderModal: nextLiveReminders.length > 0,
    });
    this.registerActiveAutomation("reminder");
    return true;
  }

  removeLiveReminder(id) {
    const entryId = String(id || "");
    const nextLiveReminders = (Array.isArray(this.state.liveReminders) ? this.state.liveReminders : [])
      .filter((entry) => String(entry.id) !== entryId);

    this.updateState({
      liveReminders: nextLiveReminders,
      reminderSnapshot: nextLiveReminders[0]?.snapshot || {},
      showReminderModal: nextLiveReminders.length > 0,
    });

    if (!nextLiveReminders.length) {
      this.unregisterActiveAutomation("reminder");
    }
  }

  clearLiveReminders() {
    this.updateState({
      liveReminders: [],
      reminderSnapshot: {},
      showReminderModal: false,
    });
    this.unregisterActiveAutomation("reminder");
  }

  setReminderSnapshot(snapshot) {
    this.updateState({ reminderSnapshot: snapshot });
  }

  setShowReminderModal(show) {
    if (show) {
      if (hasSnapshotContent(this.state.reminderSnapshot)) {
        this.upsertLiveReminder(
          getLiveAutomationId(this.state.reminderSnapshot, "reminder"),
          this.state.reminderSnapshot
        );
        return;
      }
      this.updateState({ showReminderModal: true });
      this.registerActiveAutomation("reminder");
      return;
    }
    this.clearLiveReminders();
  }

  // AutoMusic methods
  setActiveSegments(segments) {
    this.updateState({ activeSegments: segments });
  }

  setSegmentSnapshot(snapshot) {
    this.updateState({ segmentSnapshot: snapshot });
  }

  setShowMusicModal(show) {
    this.updateState({ showMusicModal: show });
    if (show) {
      this.registerActiveAutomation('music');
    } else {
      this.unregisterActiveAutomation('music');
    }
  }

  setShowVideoMusicModal(show) {
    this.updateState({ showVideoMusicModal: show });
    if (show) {
      this.registerActiveAutomation('music');
    } else {
      this.unregisterActiveAutomation('music');
    }
  }

  setIsPaused(paused) {
    this.updateState({ isPaused: paused });
  }

  // Routine birthday methods
  setBirthdays(birthdays) {
    this.updateState({ birthdays });
  }

  setShowBirthdayModal(show) {
    this.updateState({ showBirthdayModal: show });
    if (show) {
      this.registerActiveAutomation('birthday');
    } else {
      this.unregisterActiveAutomation('birthday');
    }
  }

  // Shared methods
  setCurrentSystemTime(time) {
    this.updateState({ currentSystemTime: time });
  }

  setThrottled(throttled) {
    this.updateState({ throttled });
  }

  // Get current state
  getState() {
    return this.state;
  }

  // Clear all state (useful for testing or reset)
  clearState() {
    this.state = {
      nextClass: null,
      timeUntilNavigation: null,
      navigationScheduled: false,
      activeCountdowns: [],
      liveCountdowns: [],
      countdownSnapshot: {},
      showCountdownModal: false,
      isFlashing: false,
      activeReminders: [],
      liveReminders: [],
      reminderSnapshot: {},
      showReminderModal: false,
      activeSegments: [],
      segmentSnapshot: {},
      showMusicModal: false,
      showVideoMusicModal: false,
      isPaused: false,
      birthdays: [],
      showBirthdayModal: false,
      currentSystemTime: new Date(),
      throttled: false,
      activeAutomations: []
    };
    this.saveToStorage();
    this.notify();
  }
}

// Create singleton instance
const automationStateManager = new AutomationStateManager();

export default automationStateManager;
